import { Inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, filter } from 'rxjs';
import { CurrentModuleOptions, NavOptions } from '../constants-types';
import { CURRENT_APP_TYPE } from '../core-data-model/InjectionTokens';
import { AppType } from '../shared/Enums';
import { ModuleAccessService } from '../system-services';

@Injectable({
    providedIn: 'root'
})
export class SiteNavigationService {
    currentClientModule$: BehaviorSubject<string> = new BehaviorSubject(
        this.clientAppModule
    );
    constructor(
        @Inject(CURRENT_APP_TYPE) public currentAppType: AppType,
        private router: Router,
        private moduleAccess: ModuleAccessService
    ) {
        // After Every NavigationEnd we update the current client module
        this.router.events
            .pipe(filter((event: any) => event instanceof NavigationEnd))
            .subscribe((e: NavigationEnd) => {
                this.currentClientModule$.next(this.clientAppModule);
            });
    }

    public get isInDesign(): boolean {
        return (
            this.currentAppType === AppType.Design ||
            this.router.url.includes('/design')
        );
    }

    // Specific use case to handle the fact that design and design 2 live on the same route
    public get isInDesign2(): boolean {
        return (
            (this.isInDesign && this.moduleAccess.hasDesign2) ||
            this.router.url.includes('/unlock/design') ||
            this.router.url.includes('/expired/design')
        );
    }

    public get isInHire(): boolean {
        return (
            this.currentAppType === AppType.Hire ||
            this.router.url.includes('/unlock/hire') ||
            this.router.url.includes('/expired/hire') ||
            this.router.url.includes('/hire')
        );
    }

    // Should work in both SPA and Diagnose Sepdep
    public get isInDiagnose(): boolean {
        return (
            this.currentAppType === AppType.Diagnose ||
            this.router.url.includes('/unlock/diagnose') ||
            this.router.url.includes('/expired/diagnose') ||
            this.router.url.includes('/diagnose')
        );
    }

    // Usable once Inspire SepDep is available
    public get isInInspire(): boolean {
        return this.isInMain && this.router.url.includes('/inspire');
    }

    // Specific use case to handle the fact that inspire and inspire 2 live on the same route
    public get isInInspire2(): boolean {
        return (
            this.currentAppType === AppType.Inspire ||
            this.router.url.includes('/inspire/client') ||
            this.router.url.includes('/unlock/inspire') ||
            this.router.url.includes('/expired/inspire')
        );
    }

    public get isInMain(): boolean {
        return this.currentAppType === AppType.Main;
    }

    public get isInPlatform(): boolean {
        return this.currentAppType === AppType.Platform;
    }

    public get isInSubscriptionsAndBilling(): boolean {
        return (
            this.router.url.includes('billing') ||
            this.router.url.includes('subscription')
        );
    }

    public get isInTapestryPlayground(): boolean {
        return this.router.url.includes('tapestryPlayground');
    }

    public get isInAdmin(): boolean {
        return this.currentAppType === AppType.Admin;
    }

    public get isInGroupAnalytics(): boolean {
        return this.currentAppType === AppType.GroupAnalytics;
    }

    public get isInSellerExperience(): boolean {
        return this.currentAppType === AppType.SellerExperience;
    }

    /** This method is strictly used in the Global-Nav context and not to be used for client module check */
    public get currentModule(): string {
        if (this.isInDiagnose) {
            return NavOptions.Diagnose;
        } else if (this.isInHire) {
            return NavOptions.Hire;
        } else if (this.isInDesign || this.isInDesign2) {
            return NavOptions.Design;
        } else if (this.isInInspire || this.isInInspire2) {
            return NavOptions.Inspire;
        } else {
            return NavOptions.Admin;
        }
    }

    /**  This method is used to identify what client module is currently active */
    public get clientAppModule(): string {
        if (this.isInDiagnose) {
            return CurrentModuleOptions.Diagnose;
        } else if (this.isInHire) {
            return CurrentModuleOptions.Hire;
        } else if (this.isInDesign || this.isInDesign2) {
            return CurrentModuleOptions.Design;
        } else if (this.isInInspire || this.isInInspire2) {
            return CurrentModuleOptions.Inspire;
        } else if (this.isInAdmin) {
            return CurrentModuleOptions.Admin;
        } else {
            return CurrentModuleOptions.Platform;
        }
    }

    public isInOnboarding(): boolean {
        return this.router.url.includes('onboarding');
    }

    public isInExpired(): boolean {
        return this.router.url.includes('expired');
    }

    getBaseRoute() {
        return '/';
    }
}
